/**
 * useTimer — 3-tier timer system with absolute timestamps.
 *
 * Tier 1: Native OS timer (Android Intent) — most reliable
 * Tier 2: Service Worker notification — works in background
 * Tier 3: In-app timer with Web Worker ticks — visual display
 *
 * Features: pause/resume, +/-1min, localStorage persistence (survives app close),
 * absolute timestamps (no drift), Web Audio beep, vibration, wake lock.
 * Expired timers are cleaned up on restore (no stale finished timers).
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// ── Types ──

export interface TimerState {
  id: string;
  recipeId?: number;
  stepNumber?: number;
  label: string;
  durationMs: number;
  startedAt: number;       // Date.now() at start
  pausedAt: number | null;
  pausedDuration: number;  // cumulative paused time in ms
  status: 'running' | 'paused' | 'finished';
}

const MAX_TIMERS = 5;
const STORAGE_KEY = 'plate_active_timers';

// ── Helpers ──

export function getRemainingMs(timer: TimerState): number {
  if (timer.status === 'finished') return 0;
  if (timer.status === 'paused' && timer.pausedAt) {
    const elapsed = timer.pausedAt - timer.startedAt - timer.pausedDuration;
    return Math.max(timer.durationMs - elapsed, 0);
  }
  const elapsed = Date.now() - timer.startedAt - timer.pausedDuration;
  return Math.max(timer.durationMs - elapsed, 0);
}

export function getRemainingSeconds(timer: TimerState): number {
  return Math.ceil(getRemainingMs(timer) / 1000);
}

export function formatTime(seconds: number): string {
  if (seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ── Audio ──

function playBeep() {
  try {
    const ctx = new AudioContext();
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.frequency.value = 880;
    osc1.type = 'sine';
    gain1.gain.value = 0.3;
    osc1.start();
    gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1);
    osc1.stop(ctx.currentTime + 1);
    setTimeout(() => {
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.frequency.value = 1100;
      osc2.type = 'sine';
      gain2.gain.value = 0.3;
      osc2.start();
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.5);
      osc2.stop(ctx.currentTime + 1.5);
    }, 300);
  } catch { /* Web Audio not supported */ }
}

function vibrate() {
  navigator.vibrate?.([200, 100, 200, 100, 200]);
}

// ── Native Android Timer (Tier 1) ──

function startNativeTimerAndroid(seconds: number, label: string): boolean {
  if (!/android/i.test(navigator.userAgent)) return false;
  try {
    const intent = `intent:#Intent;action=android.intent.action.SET_TIMER;S.android.intent.extra.alarm.LENGTH=${seconds};S.android.intent.extra.alarm.MESSAGE=${encodeURIComponent(label)};S.android.intent.extra.alarm.SKIP_UI=true;end`;
    window.location.href = intent;
    return true;
  } catch {
    return false;
  }
}

// ── Service Worker Messages (Tier 2) ──

function swMessage(msg: Record<string, unknown>) {
  navigator.serviceWorker?.controller?.postMessage(msg);
}

function swScheduleTimer(timer: TimerState) {
  const remainMs = getRemainingMs(timer);
  swMessage({
    type: 'SCHEDULE_TIMER',
    id: timer.id,
    expiresAt: Date.now() + remainMs,
    label: timer.label,
    recipeId: timer.recipeId,
    stepNumber: timer.stepNumber,
  });
}

// ── Notification Permission ──

let _notifAsked = false;

async function ensureNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  if (_notifAsked) return false;
  _notifAsked = true;
  return (await Notification.requestPermission()) === 'granted';
}

// ── Persistence ──

function persist(timers: Map<string, TimerState>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(timers.values())));
  } catch { /* ignore */ }
}

function restore(): Map<string, TimerState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const arr: TimerState[] = JSON.parse(raw);
    const map = new Map<string, TimerState>();
    for (const t of arr) {
      if (t.status === 'running' && getRemainingMs(t) <= 0) {
        // Expired while app was closed — discard silently
        continue;
      }
      if (t.status === 'finished') {
        // Already finished — discard on reload
        continue;
      }
      // Running timers with remaining time: keep running (absolute timestamps still valid)
      // Paused timers: keep as-is
      map.set(t.id, t);
    }
    // Clean up storage if we discarded any
    if (map.size < arr.length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(map.values())));
    }
    return map;
  } catch {
    return new Map();
  }
}

// ── Hook ──

export function useTimer() {
  const [timers, setTimers] = useState<Map<string, TimerState>>(() => restore());
  const [tick, setTick] = useState(0); // force re-render every second
  const workerRef = useRef<Worker | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  // Persist on every change
  useEffect(() => { persist(timers); }, [timers]);

  // Web Worker for ticks
  useEffect(() => {
    try {
      const worker = new Worker('/timer-worker.js');
      workerRef.current = worker;
      worker.onmessage = () => {
        setTick(t => t + 1);
      };
      return () => worker.terminate();
    } catch {
      // Fallback to setInterval if Worker not available
      const interval = setInterval(() => setTick(t => t + 1), 1000);
      return () => clearInterval(interval);
    }
  }, []);

  // Start/stop worker based on active timers
  useEffect(() => {
    const hasActive = Array.from(timers.values()).some(t => t.status === 'running' || t.status === 'paused');
    workerRef.current?.postMessage(hasActive ? 'start' : 'stop');
  }, [timers]);

  // Check for expired timers on each tick
  useEffect(() => {
    let changed = false;
    const next = new Map(timers);
    for (const [id, timer] of next) {
      if (timer.status === 'running' && getRemainingMs(timer) <= 0) {
        next.set(id, { ...timer, status: 'finished' });
        playBeep();
        vibrate();
        changed = true;
      }
    }
    if (changed) setTimers(next);
  }, [tick]);

  // Wake lock
  useEffect(() => {
    const hasRunning = Array.from(timers.values()).some(t => t.status === 'running');
    if (hasRunning && !wakeLockRef.current) {
      navigator.wakeLock?.request('screen').then(l => { wakeLockRef.current = l; }).catch(() => {});
    } else if (!hasRunning && wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  }, [timers]);

  // Visibility change — update displays + keepalive
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        setTick(t => t + 1); // force re-render with correct times
        swMessage({ type: 'KEEPALIVE_PING' });
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // Listen for SW timer-expired messages
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'TIMER_EXPIRED') {
        setTimers(prev => {
          const next = new Map(prev);
          const timer = next.get(event.data.id);
          if (timer && timer.status !== 'finished') {
            next.set(event.data.id, { ...timer, status: 'finished' });
            playBeep();
            vibrate();
          }
          return next;
        });
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, []);

  // Cleanup wake lock on unmount
  useEffect(() => {
    return () => {
      wakeLockRef.current?.release().catch(() => {});
    };
  }, []);

  // ── Actions ──

  const startTimer = useCallback((id: string, seconds: number, label: string, recipeId?: number, stepNumber?: number) => {
    setTimers(prev => {
      if (prev.size >= MAX_TIMERS && !prev.has(id)) {
        return prev; // max reached
      }
      const timer: TimerState = {
        id, label, recipeId, stepNumber,
        durationMs: seconds * 1000,
        startedAt: Date.now(),
        pausedAt: null,
        pausedDuration: 0,
        status: 'running',
      };
      const next = new Map(prev);
      next.set(id, timer);

      // Tier 1: Native Android timer
      startNativeTimerAndroid(seconds, label);

      // Tier 2: SW notification
      ensureNotificationPermission().then(() => swScheduleTimer(timer));

      return next;
    });
  }, []);

  const pauseTimer = useCallback((id: string) => {
    setTimers(prev => {
      const timer = prev.get(id);
      if (!timer || timer.status !== 'running') return prev;
      const next = new Map(prev);
      next.set(id, { ...timer, status: 'paused', pausedAt: Date.now() });
      swMessage({ type: 'PAUSE_TIMER', id });
      return next;
    });
  }, []);

  const resumeTimer = useCallback((id: string) => {
    setTimers(prev => {
      const timer = prev.get(id);
      if (!timer || timer.status !== 'paused' || !timer.pausedAt) return prev;
      const additionalPause = Date.now() - timer.pausedAt;
      const updated: TimerState = {
        ...timer,
        status: 'running',
        pausedAt: null,
        pausedDuration: timer.pausedDuration + additionalPause,
      };
      const next = new Map(prev);
      next.set(id, updated);
      swMessage({ type: 'RESUME_TIMER', id });
      return next;
    });
  }, []);

  const stopTimer = useCallback((id: string) => {
    setTimers(prev => {
      const next = new Map(prev);
      next.delete(id);
      swMessage({ type: 'CANCEL_TIMER', id });
      return next;
    });
  }, []);

  const adjustTimer = useCallback((id: string, deltaMs: number) => {
    setTimers(prev => {
      const timer = prev.get(id);
      if (!timer || timer.status === 'finished') return prev;
      const updated = { ...timer, durationMs: Math.max(timer.durationMs + deltaMs, 0) };
      const next = new Map(prev);
      next.set(id, updated);
      // Reschedule SW timer
      swMessage({ type: 'CANCEL_TIMER', id });
      if (updated.status === 'running') swScheduleTimer(updated);
      return next;
    });
  }, []);

  const acknowledgeTimer = useCallback((id: string) => {
    setTimers(prev => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const activeTimers = Array.from(timers.values());
  const timerById = useCallback((id: string) => timers.get(id), [timers, tick]);

  return {
    startTimer, pauseTimer, resumeTimer, stopTimer,
    adjustTimer, acknowledgeTimer,
    activeTimers, timerById,
  };
}
